import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/Button';
import TicketsModal from '../components/TicketsModal';
import { useTicketsSingleView } from '../hooks/useTicketsSingleView';
import {
    orderService,
    OrderNotFoundError,
    OrderPaymentItem,
    OrderPaymentStatus,
    PaymentMethod,
    PaymentProvider
} from '../services/orderService';
import { PublicEventDetail, publicEventsService } from '../services/publicEventsService';
import { formatInstant } from '../utils/dates';

type DisplayStatus = 'APPROVED' | 'REVIEW' | 'PENDING' | 'REJECTED' | 'EXPIRED' | 'REFUNDED';

type PrimaryAction = 'tickets' | 'refresh' | 'event' | 'home';

interface ReturnParams {
    reference: string;
    provider: PaymentProvider;
    transactionId: string | null;
}

// While the payment is pending: every 5 s for the first 2 minutes, then every 20 s. Bold may take up to
// 10 minutes to show a transaction, so polling stops after 12 minutes and the buyer can refresh by hand.
const FAST_POLL_MS = 5_000;
const SLOW_POLL_MS = 20_000;
const FAST_POLL_WINDOW_MS = 2 * 60_000;
const MAX_POLL_MS = 12 * 60_000;

const PROVIDER_LABELS: Record<PaymentProvider, string> = {
    BOLD: 'Bold',
    WOMPI: 'Wompi',
    MERCADOPAGO: 'Mercado Pago'
};

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    CREDIT_CARD: 'Tarjeta de crédito',
    DEBIT_CARD: 'Tarjeta débito',
    CARD: 'Tarjeta',
    PSE: 'PSE',
    NEQUI: 'Nequi',
    DAVIPLATA: 'Daviplata',
    BANCOLOMBIA: 'Botón Bancolombia',
    QR: 'Código QR',
    BANK_TRANSFER: 'Transferencia bancaria',
    MERCADO_PAGO_ACCOUNT: 'Dinero en cuenta Mercado Pago',
    CASH: 'Efectivo',
    OTHER: 'Otro'
};

const STATUS_CONFIG: Record<DisplayStatus, {
    color: string;
    bgColor: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    badge: string;
    primaryButtonLabel: string;
    primaryAction: PrimaryAction;
}> = {
    APPROVED: {
        color: 'var(--st-success)',
        bgColor: 'var(--st-success-light)',
        title: '¡Pago Exitoso!',
        description: 'Tu pago fue confirmado. Te enviamos tus boletas en PDF al correo de tu compra y puedes verlas aquí una sola vez.',
        badge: 'Pagado',
        primaryButtonLabel: 'Ver mis Tickets',
        primaryAction: 'tickets',
        icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
    },
    REVIEW: {
        color: 'var(--st-warning)',
        bgColor: 'var(--st-warning-light)',
        title: 'Pago Recibido, en Revisión',
        // SuperTicket never refunds: the payment is in the organizer's account (ROADMAP.md, orders under review)
        description: 'Recibimos tu pago, pero tu orden quedó en revisión y por ahora no tiene boletas (por ejemplo, porque el tiempo de tu reserva terminó). El organizador del evento la revisará: emitirá tus boletas o te devolverá el dinero.',
        badge: 'En revisión',
        primaryButtonLabel: 'Volver al Inicio',
        primaryAction: 'home',
        icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"></path></svg>
    },
    PENDING: {
        color: 'var(--st-warning)',
        bgColor: 'var(--st-warning-light)',
        title: 'Pago Pendiente',
        description: 'Estamos esperando la confirmación de la pasarela de pago. Esto puede tomar unos minutos; esta página se actualiza sola.',
        badge: 'Pendiente',
        primaryButtonLabel: 'Actualizar Estado',
        primaryAction: 'refresh',
        icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
    },
    REJECTED: {
        color: 'var(--st-error)',
        bgColor: 'var(--st-error-light)',
        title: 'Pago Rechazado',
        description: 'La transacción no pudo completarse. Por favor verifica tus fondos o intenta con otro medio de pago.',
        badge: 'Fallido',
        primaryButtonLabel: 'Intentar de Nuevo',
        primaryAction: 'event',
        icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path></svg>
    },
    EXPIRED: {
        color: 'var(--st-error)',
        bgColor: 'var(--st-error-light)',
        title: 'Reserva Expirada',
        description: 'El tiempo para pagar terminó y las entradas reservadas se liberaron. Si alcanzaste a pagar, tu orden pasará a revisión del organizador del evento, que emitirá tus boletas o te devolverá el dinero.',
        badge: 'Expirada',
        primaryButtonLabel: 'Intentar de Nuevo',
        primaryAction: 'event',
        icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
    },
    REFUNDED: {
        color: 'var(--st-info)',
        bgColor: 'var(--st-info-light)',
        title: 'Orden Reembolsada',
        description: 'El organizador del evento reembolsó esta orden, así que no tiene boletas. Si tienes dudas sobre el reembolso, escríbele al organizador.',
        badge: 'Reembolsada',
        primaryButtonLabel: 'Volver al Inicio',
        primaryAction: 'home',
        icon: <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M3 10h10a5 5 0 015 5v2M3 10l5 5m-5-5l5-5"></path></svg>
    }
};

// Each gateway returns the buyer with its own parameters. The status they carry (bold-tx-status,
// collection_status...) is never trusted: the backend asks the gateway.
const readReturnParams = (params: URLSearchParams): ReturnParams | null => {
    const bold = params.get('bold-order-id');
    if (bold) return { reference: bold, provider: 'BOLD', transactionId: null };

    const wompi = params.get('wompi-order-id');
    if (wompi) {
        // Wompi appends ?id=<transaction>&env=... to the redirect URL; if it does not merge it with our own
        // query string, the ID ends up inside the reference value ("<reference>?id=<transaction>")
        const [reference, appended] = wompi.split('?');
        const transactionId = params.get('id') ?? new URLSearchParams(appended ?? '').get('id');
        return { reference, provider: 'WOMPI', transactionId };
    }

    const mercadoPago = params.get('mp-order-id');
    if (mercadoPago) return { reference: mercadoPago, provider: 'MERCADOPAGO', transactionId: params.get('payment_id') };

    return null;
};

const toDisplayStatus = (status: OrderPaymentStatus): DisplayStatus => {
    if (status.orderStatus === 'REFUNDED') return 'REFUNDED';
    if (status.underReview) return 'REVIEW';
    if (status.orderStatus === 'PAID') return 'APPROVED';
    if (status.paymentStatus === 'REJECTED') return 'REJECTED';
    if (status.orderStatus === 'EXPIRED' || status.orderStatus === 'CANCELLED') return 'EXPIRED';
    return 'PENDING';
};

const formatDate = (isoDate?: string) =>
    formatInstant(isoDate, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const OrderConfirmation = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [returnParams] = useState<ReturnParams | null>(() => readReturnParams(searchParams));
    const [orderStatus, setOrderStatus] = useState<OrderPaymentStatus | null>(null);
    const [event, setEvent] = useState<PublicEventDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollStartedAt = useRef<number>(Date.now());

    // Single view of the tickets: kept here so closing the modal does not lose them
    const ticketsView = useTicketsSingleView(returnParams?.reference ?? null);
    const [isTicketsOpen, setIsTicketsOpen] = useState(false);

    const stopPolling = () => {
        if (pollTimer.current) clearTimeout(pollTimer.current);
        pollTimer.current = null;
    };

    const verify = useCallback(async () => {
        if (!returnParams) return;
        stopPolling();
        try {
            const status = await orderService.verifyOrder(
                returnParams.reference, returnParams.provider, returnParams.transactionId);
            setOrderStatus(status);
            setError(null);

            const elapsed = Date.now() - pollStartedAt.current;
            if (toDisplayStatus(status) === 'PENDING' && elapsed < MAX_POLL_MS) {
                pollTimer.current = setTimeout(verify, elapsed < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS);
            }
        } catch (e) {
            setError(e instanceof OrderNotFoundError
                ? 'No encontramos la orden de este pago.'
                : 'No pudimos consultar el estado del pago. Intenta actualizar en unos segundos.');
        } finally {
            setIsLoading(false);
        }
    }, [returnParams]);

    useEffect(() => {
        if (!returnParams) {
            navigate('/');
            return;
        }
        pollStartedAt.current = Date.now();
        verify();
        return stopPolling;
    }, [returnParams, navigate, verify]);

    // Event name and stage names for the summary: the order only stores their IDs
    const eventId = orderStatus?.eventId;
    useEffect(() => {
        if (!eventId) return;
        publicEventsService.getEventById(eventId)
            .then(setEvent)
            .catch(() => setEvent(null));
    }, [eventId]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        pollStartedAt.current = Date.now();
        await verify();
        setIsRefreshing(false);
    };

    const handlePrimaryAction = (action: PrimaryAction) => {
        switch (action) {
            case 'tickets':
                setIsTicketsOpen(true);
                ticketsView.open();
                break;
            case 'refresh':
                handleRefresh();
                break;
            case 'event':
                navigate(orderStatus?.eventId ? `/events/${orderStatus.eventId}` : '/');
                break;
            default:
                navigate('/');
        }
    };

    const formatPrice = (price: number, currency = 'COP') => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
    };

    if (isLoading) {
        return (
            <div className="st-page flex flex-col items-center justify-center min-h-screen">
                <div className="st-spinner st-spinner-xl mb-4"></div>
                <p className="text-[var(--st-text-secondary)] font-medium animate-pulse">Verificando estado del pago...</p>
            </div>
        );
    }

    if (!orderStatus) {
        return (
            <div className="st-page py-12 px-4">
                <div className="st-container max-w-2xl">
                    <div className="st-card-static bg-white p-8 text-center shadow-xl">
                        <h1 className="font-display text-2xl font-bold text-[var(--st-text-primary)] mb-2">
                            No pudimos verificar tu pago
                        </h1>
                        <p className="text-[var(--st-text-secondary)] mb-6">{error}</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button className="st-btn st-btn-primary st-btn-lg w-full sm:w-auto" onClick={handleRefresh} disabled={isRefreshing}>
                                {isRefreshing ? 'Consultando...' : 'Intentar de Nuevo'}
                            </Button>
                            <Button variant="ghost" className="st-btn st-btn-ghost st-btn-lg w-full sm:w-auto" onClick={() => navigate('/')}>
                                Volver al Inicio
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const displayStatus = toDisplayStatus(orderStatus);
    const uiConfig = STATUS_CONFIG[displayStatus];
    const provider = orderStatus.provider ?? returnParams?.provider;
    // The order keeps the names it was sold with; older orders fall back to the event's current stages
    const stageName = (item: OrderPaymentItem) =>
        item.stageName ?? event?.stages?.find(stage => stage.id === item.stageId)?.name ?? 'Entrada';
    const eventName = orderStatus.eventName ?? event?.name ?? 'Evento';
    const refundContacts = [orderStatus.refundContactEmail ?? event?.refundContactEmail,
        orderStatus.refundContactPhone ?? event?.refundContactPhone].filter(Boolean).join(' · ');
    const showServiceFee = orderStatus.feePayer === 'BUYER' && orderStatus.serviceFee > 0;
    const currency = orderStatus.currency;

    return (
        <div className="st-page py-12 px-4">
            <div className="st-container max-w-2xl">
                <div className="st-card-static bg-white overflow-hidden shadow-xl animate-fade-in-up">
                    <div
                        className="p-8 text-center border-b"
                        style={{
                            backgroundColor: uiConfig.bgColor,
                            borderColor: `${uiConfig.color}33`
                        }}
                    >
                        <div
                            className="w-20 h-20 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg animate-scale-in"
                            style={{ backgroundColor: uiConfig.color }}
                        >
                            {uiConfig.icon}
                        </div>
                        <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)] mb-2">
                            {uiConfig.title}
                        </h1>
                        <p className="text-[var(--st-text-secondary)] max-w-md mx-auto">
                            {uiConfig.description}
                        </p>
                        {(displayStatus === 'REVIEW' || displayStatus === 'REFUNDED') && refundContacts && (
                            <p className="text-sm text-[var(--st-text-secondary)] max-w-md mx-auto mt-3">
                                Contacto del organizador: <span className="font-semibold text-[var(--st-text-primary)]">{refundContacts}</span>
                            </p>
                        )}
                        {error && (
                            <p className="text-sm text-[var(--st-error)] mt-3">{error}</p>
                        )}
                    </div>

                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <div>
                                <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider mb-1">Referencia</p>
                                <p className="font-display text-sm font-bold text-[var(--st-text-primary)] break-all">{orderStatus.reference}</p>
                            </div>
                            <div className="text-left md:text-right">
                                <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider mb-1">Fecha</p>
                                <p className="text-[var(--st-text-secondary)] font-medium">
                                    {formatDate(orderStatus.paidAt ?? orderStatus.createdAt)}
                                </p>
                            </div>
                        </div>

                        <div className="bg-[var(--st-bg-tertiary)] rounded-xl p-4 mb-8 flex items-center gap-4 border border-[var(--st-border)]">
                            <div className="w-16 h-16 rounded-lg bg-white border border-gray-100 flex-shrink-0 flex items-center justify-center text-2xl shadow-sm">
                                🎟️
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-[var(--st-text-primary)]">{eventName}</h3>
                                {orderStatus.items.map(item => (
                                    <p key={item.stageId} className="text-sm text-[var(--st-text-secondary)]">
                                        {stageName(item)} × {item.quantity}
                                    </p>
                                ))}
                            </div>
                            <div className="text-right">
                                <span
                                    className="st-badge mb-1 inline-block"
                                    style={{
                                        backgroundColor: uiConfig.bgColor,
                                        color: uiConfig.color
                                    }}
                                >
                                    {uiConfig.badge}
                                </span>
                                <p className="font-bold text-[var(--st-text-primary)]">{formatPrice(orderStatus.amount, currency)}</p>
                            </div>
                        </div>

                        <div className="border-t border-[var(--st-border)] pt-6 mb-8">
                            <h4 className="font-bold text-[var(--st-text-primary)] mb-4">Resumen de la Compra</h4>
                            <div className="space-y-3">
                                {orderStatus.items.map(item => (
                                    <div key={item.stageId} className="flex justify-between gap-4">
                                        <span className="text-[var(--st-text-secondary)]">
                                            {stageName(item)} × {item.quantity}
                                            {item.unitPrice != null && <span className="text-[var(--st-text-muted)]"> ({formatPrice(item.unitPrice, currency)} c/u)</span>}
                                        </span>
                                        {item.unitPrice != null && (
                                            <span className="font-medium text-[var(--st-text-primary)]">{formatPrice(item.unitPrice * item.quantity, currency)}</span>
                                        )}
                                    </div>
                                ))}
                                <div className="flex justify-between border-t border-[var(--st-border)] pt-3">
                                    <span className="text-[var(--st-text-secondary)]">Subtotal boletas</span>
                                    <span className="font-medium text-[var(--st-text-primary)]">{formatPrice(orderStatus.subtotal, currency)}</span>
                                </div>
                                {showServiceFee && (
                                    <div className="flex justify-between">
                                        <span className="text-[var(--st-text-secondary)]">Cargo por servicio SuperTicket</span>
                                        <span className="font-medium text-[var(--st-text-primary)]">{formatPrice(orderStatus.serviceFee, currency)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-[var(--st-text-secondary)]">Total</span>
                                    <span className="font-display font-bold text-xl text-[var(--st-accent-primary)]">
                                        {formatPrice(orderStatus.amount, currency)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-[var(--st-border)] pt-6 mb-8">
                            <h4 className="font-bold text-[var(--st-text-primary)] mb-4">Detalles del Pago</h4>
                            <div className="space-y-3">
                                {provider && (
                                    <div className="flex justify-between">
                                        <span className="text-[var(--st-text-secondary)]">Pasarela</span>
                                        <span className="font-medium text-[var(--st-text-primary)]">{PROVIDER_LABELS[provider]}</span>
                                    </div>
                                )}
                                {orderStatus.paymentMethod && (
                                    <div className="flex justify-between">
                                        <span className="text-[var(--st-text-secondary)]">Medio de pago</span>
                                        <span className="font-medium text-[var(--st-text-primary)]">
                                            {PAYMENT_METHOD_LABELS[orderStatus.paymentMethod] ?? PAYMENT_METHOD_LABELS.OTHER}
                                        </span>
                                    </div>
                                )}
                                {orderStatus.transactionId && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-[var(--st-text-secondary)]">Transacción</span>
                                        <span className="font-medium text-[var(--st-text-primary)] break-all text-right">{orderStatus.transactionId}</span>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-[var(--st-text-muted)] mt-4">
                                Los reembolsos los gestiona directamente el organizador del evento
                                {refundContacts
                                    ? <>: <span className="font-medium text-[var(--st-text-secondary)]">{refundContacts}</span></>
                                    : '.'}
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button
                                variant={displayStatus === 'APPROVED' ? 'primary' : 'secondary'}
                                // st-btn-primary gives the same hover as "Comprar Entradas" (slightly darker, lifted)
                                className={`st-btn ${displayStatus === 'APPROVED' ? 'st-btn-primary' : ''} st-btn-lg w-full sm:w-auto`}
                                onClick={() => handlePrimaryAction(uiConfig.primaryAction)}
                                disabled={isRefreshing}
                            >
                                {isRefreshing ? 'Consultando...' : uiConfig.primaryButtonLabel}
                            </Button>

                            {uiConfig.primaryAction !== 'home' && (
                                <Button
                                    variant="ghost"
                                    className="st-btn st-btn-ghost st-btn-lg w-full sm:w-auto"
                                    onClick={() => navigate('/')}
                                >
                                    Volver al Inicio
                                </Button>
                            )}
                        </div>

                    </div>
                </div>

                <p className="text-center text-xs text-[var(--st-text-muted)] mt-8">
                    Orden {orderStatus.orderId} • {new Date().getFullYear()} SuperTicket
                </p>
            </div>

            <TicketsModal isOpen={isTicketsOpen} onClose={() => setIsTicketsOpen(false)} tickets={ticketsView} />
        </div>
    );
};

export default OrderConfirmation;
